import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Task } from '../lib/types';
import { Sheet } from '../components/Sheet';
import { Amount } from '../components/Currency';
import { haptic, tg } from '../lib/telegram';
import { useApp } from '../App';

/** Раздел 6. Проверка живёт на бэкенде; здесь только запуск и статусы. */

const GROUPS: { key: Task['category']; title: string; note?: string }[] = [
  { key: 'daily', title: 'Каждый день' },
  { key: 'weekly', title: 'На этой неделе' },
  { key: 'once', title: 'Разовые' },
  { key: 'partner', title: 'От партнёров' },
];

export function TasksScreen() {
  const { refresh, toast } = useApp();
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [codeFor, setCodeFor] = useState<Task | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  // Задания «на доверии» со ссылкой сдаются в два шага: сперва человек уходит
  // за файлом, и только потом подтверждает. Иначе награда падала бы раньше,
  // чем он что-то сделал.
  const [opened, setOpened] = useState<number[]>([]);

  const load = () => api.get<Task[]>('/tasks').then(setTasks).catch(() => {});
  useEffect(() => { void load(); }, []);

  async function verify(task: Task, codeWord?: string) {
    setBusy(task.id);
    try {
      const res = await api.post<{ status: string; reason?: string; reward?: { coins: number; spins: number } }>(
        `/tasks/${task.id}/verify`, { code: codeWord },
      );
      if (res.status === 'approved') {
        haptic.ok();
        toast(res.reward ? `Готово: +${res.reward.coins} коинов` : 'Готово');
        setCodeFor(null);
        setCode('');
        await Promise.all([load(), refresh()]);
      } else if (res.status === 'pending') {
        toast('Отправлено на проверку, ответ придёт в бот');
        await load();
      } else if (res.status === 'already_done') {
        await load();
      } else {
        haptic.no();
        toast(res.reason ?? 'Не засчитано', 'bad');
      }
    } catch (e) {
      toast((e as ApiError).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  if (!tasks) return <Skeleton />;

  return (
    <div className="rise space-y-7 pb-4">
      {GROUPS.map((g) => {
        const list = tasks.filter((t) => t.category === g.key);
        if (list.length === 0) return null;
        return (
          <section key={g.key}>
            <h2 className="label mb-3">{g.title}</h2>
            <div className="space-y-2">
              {list.map((t) => (
                <TaskCard
                  key={t.id} task={t} busy={busy === t.id}
                  opened={opened.includes(t.id)}
                  onAct={() => {
                    haptic.tap();
                    if (t.locked) return;
                    if (t.verify_type === 'code_word') { setCodeFor(t); setCode(''); return; }
                    if (t.verify_type === 'honor' && t.action_url && !opened.includes(t.id)) {
                      tg.openLink(t.action_url);
                      setOpened((o) => [...o, t.id]);
                      return;
                    }
                    if (t.action_url && t.verify_type !== 'honor') tg.openLink(t.action_url);
                    void verify(t);
                  }}
                />
              ))}
            </div>
          </section>
        );
      })}

      <Sheet open={!!codeFor} onClose={() => setCodeFor(null)} title={codeFor?.title}>
        <p className="mb-3 text-sm text-ash">{codeFor?.description ?? 'Введи кодовое слово.'}</p>
        {codeFor?.action_url && (
          <button className="btn-ghost mb-3 w-full" onClick={() => tg.openLink(codeFor.action_url!)}>
            Открыть и найти код
          </button>
        )}
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Кодовое слово"
          autoCapitalize="none"
          className="mb-3 w-full rounded-xl border border-line bg-raised px-4 py-3 text-base
                     placeholder:text-ash focus:border-turq focus:outline-none"
        />
        <button className="btn-primary w-full" disabled={!code.trim() || busy === codeFor?.id}
          onClick={() => codeFor && verify(codeFor, code.trim())}>
          Проверить
        </button>
      </Sheet>
    </div>
  );
}

function TaskCard(
  { task, busy, opened, onAct }:
  { task: Task; busy: boolean; opened: boolean; onAct: () => void },
) {
  const done = task.status === 'approved';
  const pending = task.status === 'pending';
  const locked = task.locked && !done;

  const label = busy ? '…'
    : task.verify_type === 'honor'
      ? (task.action_url ? (opened ? 'Я добавил' : 'Получить') : 'Я сделал')
      : task.action_url ? 'Перейти' : 'Проверить';

  return (
    <div className={`card flex items-center gap-3 p-3.5 ${done ? 'opacity-55' : ''} ${locked ? 'opacity-70' : ''}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-semibold">{task.title}</p>
        {task.description && !done && !locked && (
          <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-ash">{task.description}</p>
        )}
        {locked && task.unlock_hint && (
          <p className="mt-0.5 text-xs leading-relaxed text-gold/90">{task.unlock_hint}</p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
          {task.reward_coins > 0 && <Amount kind="coin" value={task.reward_coins} size={13} />}
          {task.reward_spins > 0 && <Amount kind="spin" value={task.reward_spins} size={13} />}
          {task.reward_crystals > 0 && <Amount kind="crystal" value={task.reward_crystals} size={13} />}
          {task.reward_coins === 0 && task.reward_spins === 0 && task.reward_crystals === 0 && (
            <span className="text-xs text-ash">Награда приходит стриком</span>
          )}
        </div>
      </div>

      {done ? (
        <span className="shrink-0 text-turq" aria-label="Выполнено">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="m5 12.5 4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      ) : pending ? (
        <span className="shrink-0 rounded-lg border border-line px-2.5 py-1.5 text-xs text-ash">На проверке</span>
      ) : locked ? (
        <span className="shrink-0 text-ash" aria-label="Пока закрыто">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="10.5" width="16" height="10.5" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 10.5V7.5a4 4 0 1 1 8 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      ) : (
        <button className="btn-ghost shrink-0 px-3 py-2 text-sm" onClick={onAct} disabled={busy}>
          {label}
        </button>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2">
      {[...Array(6)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-surface" />)}
    </div>
  );
}
